import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, BookUser, Building2, Factory, FileCode2, Loader2, Plus, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { errMsg, todayIST } from '@/services/staffAttendanceApi';
import { ewbApi, holdsApi } from '@/services/ewayBillApi';
import { isDiscarded, MockBanner, ModeBadge } from '@/components/ewayBill/shared';
import { BILL_TABS, EwbTable, isWaiting } from '@/components/ewayBill/EwbTable';
import { PortalJsonBar } from '@/components/ewayBill/PortalJsonBar';
import { BulkPrintBar } from '@/components/ewayBill/BulkPrintBar';
import { TallyPushSwitch } from '@/components/ewayBill/TallyPushSwitch';
import { EwbHoldsPanel } from '@/components/ewayBill/EwbHoldsPanel';
import { DocUploadButton } from '@/components/ewayBill/DocUploadButton';
import { EwbDetailDialog } from '@/components/ewayBill/EwbDetailDialog';
import { NewEwbDialog } from '@/components/ewayBill/NewEwbDialog';
import { SenderUnitsDialog } from '@/components/ewayBill/SenderUnitsDialog';
import { TallyLinkCard } from '@/components/ewayBill/TallyLinkCard';
import { TallySetupDialog } from '@/components/ewayBill/TallySetupDialog';
import { GstinPartiesDialog } from '@/components/ewayBill/GstinPartiesDialog';
import { GateRuleSwitch } from '@/components/ewayBill/GateRuleSwitch';
import { TallyWaitingCard } from '@/components/ewayBill/TallyWaitingCard';

const monthStart = () => `${todayIST().slice(0, 8)}01`;
const sel = 'h-10 rounded-md border border-gray-300 bg-white px-3 text-sm';

const tile = (l, n, cls, id) => ({ l, n, cls, id });
const countTiles = (rows, viaTally, portal) => {
  const live = rows.filter((b) => b.status === 'ACTIVE' && !b.is_expired);
  const is = (s) => rows.filter((b) => b.status === s).length;
  if (portal) {
    return [tile('Waiting for e-way bill', rows.filter(isWaiting).length, 'text-amber-700', 'waiting'), tile('Active (not expired)', live.length, 'text-emerald-700', 'active'),
      tile('Part B pending', live.filter((b) => b.part_b_pending).length, 'text-amber-600', 'partb'),
      tile('Expired', rows.filter((b) => b.is_expired).length, 'text-gray-600', 'expired'),
      tile('Cancelled', rows.filter((b) => b.status === 'CANCELLED' && !isDiscarded(b)).length, 'text-rose-600', 'cancelled')];
  }
  return viaTally
    ? [tile('Waiting for Tally link', is('QUEUED'), 'text-slate-600', 'waiting'), tile('In Tally — to send', is('IN_TALLY'), 'text-sky-700', 'intally'),
      tile('Active', live.length, 'text-emerald-700', 'active'), tile('Tally errors', is('TALLY_ERROR'), 'text-rose-600', 'errors'),
      tile('Cancelled', rows.filter((b) => b.status === 'CANCELLED' && !isDiscarded(b)).length, 'text-gray-600', 'cancelled')]
    : [tile('Active', live.length, 'text-emerald-700', 'active'),
      tile('Expiring in 24h', live.filter((b) => new Date(b.valid_upto) - Date.now() < 864e5).length, 'text-amber-600', 'expiring'),
      tile('Expired', rows.filter((b) => b.is_expired).length, 'text-gray-600', 'expired'), tile('Cancelled', is('CANCELLED'), 'text-rose-600', 'cancelled')];
};
const Counts = ({ rows, viaTally, portal }) => (
  <div className={`grid grid-cols-2 gap-3 ${viaTally || portal ? 'md:grid-cols-5' : 'md:grid-cols-4'}`} data-testid="ewb-counts">
    {countTiles(rows, viaTally, portal).map((c) => (
      <div key={c.id} className="rounded-xl bg-white shadow-sm p-3"><div className="text-[11px] uppercase tracking-wide text-gray-500">{c.l}</div>
        <div className={`text-2xl font-bold tabular-nums ${c.cls}`} data-testid={`ewb-count-${c.id}`}>{c.n}</div></div>
    ))}
  </div>
);
const TabBtn = ({ on, onClick, testId, children }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className={`-mb-px flex items-center border-b-2 px-4 py-2 text-sm font-medium transition-colors ${on ? 'border-[#2D5F4F] text-[#1A1F2E]' : 'border-transparent text-gray-500 hover:text-[#1A1F2E]'}`}>
    {children}
  </button>
);

const UploadOnly = () => (
  <div className="max-w-2xl rounded-xl bg-white p-5 shadow-sm space-y-3" data-testid="ewb-upload-only">
    <div className="font-semibold text-[#1A1F2E]">Upload bulk Sales Invoice PDF</div>
    <p className="text-sm text-gray-600">Each invoice in the PDF is split out, linked to its DC and made into an e-way bill with the transporter and distance filled in.
      Invoices that can't go yet are held for the E-Way Bill team under Needs attention.</p>
    <DocUploadButton kind="customer_invoice" onDone={() => {}} />
  </div>
);

export default function EwayBillsPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [cfg, setCfg] = useState(null);
  const [denied, setDenied] = useState('');
  const [f, setF] = useState({ date_from: monthStart(), date_to: todayIST(), doc_type: '', q: '' });
  const [rows, setRows] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [newOpen, setNewOpen] = useState(!!params.get('doc_no'));
  const [prefill, setPrefill] = useState({ doc_type: params.get('doc_type'), doc_no: params.get('doc_no') });
  const [tab, setTabRaw] = useState(params.get('tab') === 'holds' ? 'holds' : null);
  const [holdCount, setHoldCount] = useState(null);
  const [holdTick, setHoldTick] = useState(0);
  const [unitsOpen, setUnitsOpen] = useState(false);
  const [tallyOpen, setTallyOpen] = useState(false);
  const [gstinOpen, setGstinOpen] = useState(false);
  const [tick, setTick] = useState(0);
  const viaTally = cfg?.route === 'tally';
  const portal = cfg?.route === 'portal';
  const canGen = !!cfg?.can_generate;
  const [picked, setPicked] = useState(new Set());
  const curTab = tab || (cfg?.route === 'gsp' ? 'active' : 'waiting');
  const setTab = (t) => { setTabRaw(t); setPicked(new Set()); };
  const billTab = BILL_TABS.find((t) => t.id === curTab);
  const tabRows = billTab ? (rows || []).filter(billTab.has) : [];
  const loadCfg = () => ewbApi.config().then(setCfg).catch((e) => setDenied(errMsg(e, 'No access')));
  useEffect(() => { loadCfg(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const waiting = (rows || []).filter(isWaiting);
  const togglePick = (id) => setPicked((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const load = useCallback(() => {
    ewbApi.list(Object.fromEntries(Object.entries(f).filter(([, v]) => v))).then(setRows).catch((e) => toast.error(errMsg(e, 'Failed to load')));
  }, [JSON.stringify(f)]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (canGen) load(); }, [canGen, load]);
  useEffect(() => { if (canGen) holdsApi.count().then(setHoldCount).catch(() => {}); }, [canGen, tick]);
  const [refresh, setRefresh] = useState(0);
  const lastCounts = useRef(null);
  const onCounts = (c) => {
    const k = JSON.stringify(c);
    if (lastCounts.current !== null && lastCounts.current !== k) { load(); setRefresh((n) => n + 1); }
    lastCounts.current = k;
  };
  const closeNew = () => { setNewOpen(false); setHoldTick((n) => n + 1); load(); if (params.get('doc_no')) setParams({}, { replace: true }); };
  const openNew = (p = {}) => { setPrefill(p); setNewOpen(true); };
  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="eway-bills-page">
      <header className="bg-[#1A1F2E] text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-2 sm:px-4 md:px-6 h-16 flex items-center gap-2 sm:gap-4">
          <Button variant="ghost" onClick={() => navigate('/admin/dashboard')} className="shrink-0 text-white hover:bg-white/10" data-testid="back-button"><ArrowLeft className="w-5 h-5" /></Button>
          <h1 className="text-lg sm:text-xl font-bold whitespace-nowrap">E-Way Bills</h1>
          {cfg && <span className="hidden md:inline-flex"><ModeBadge mode={cfg.mode} testId="ewb-mode-badge" /></span>}
          <div className="ml-auto flex shrink-0 items-center gap-1 sm:gap-2">
            {cfg?.can_settings && <Button variant="ghost" onClick={() => setUnitsOpen(true)} className="px-2 sm:px-4 text-white hover:bg-white/10" title="Sender units" data-testid="ewb-units-btn">
              <Building2 className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Sender units</span></Button>}
            {canGen && <Button variant="ghost" onClick={() => navigate('/admin/jobber-master')} className="px-2 sm:px-4 text-white hover:bg-white/10" title="Jobber Master" data-testid="ewb-jobbers-btn">
              <Factory className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Jobbers</span></Button>}
            {canGen && <Button variant="ghost" onClick={() => navigate('/eway-bills/suppliers')} className="px-2 sm:px-4 text-white hover:bg-white/10" title="Suppliers" data-testid="ewb-suppliers-btn">
              <BookUser className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Suppliers</span></Button>}
            {canGen && <Button variant="ghost" onClick={() => navigate('/eway-bills/pt-files')} className="px-2 sm:px-4 text-white hover:bg-white/10" title="Distributor PT files" data-testid="ewb-pt-files-btn">
              <FileCode2 className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">PT Files</span></Button>}
            {canGen && <Button onClick={() => openNew({})} className="px-3 sm:px-4 bg-amber-500 hover:bg-amber-600 text-[#1A1F2E]" title="New e-way bill" data-testid="ewb-new-btn">
              <Plus className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">New e-way bill</span></Button>}
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6 space-y-4">
        {denied ? (
          <div className="text-center py-16 text-gray-500 space-y-2" data-testid="ewb-no-access"><ShieldAlert className="w-10 h-10 mx-auto text-amber-500" /><p>{denied}</p></div>
        ) : !cfg ? <Loader2 className="w-6 h-6 mx-auto my-16 animate-spin text-amber-600" /> : !canGen ? <UploadOnly /> : (
          <>
            <MockBanner cfg={cfg} />
            {cfg && <span className="md:hidden"><ModeBadge mode={cfg.mode} testId="ewb-mode-badge-mobile" /></span>}
            <GateRuleSwitch canEdit={cfg.can_settings} />
            {cfg.tally_route && <TallyPushSwitch canEdit={cfg.can_settings} onChanged={() => { loadCfg(); setTick((n) => n + 1); }} />}
            {cfg.can_settings && <TallyWaitingCard tick={tick} onDeleted={() => { load(); setTick((n) => n + 1); }} />}
            {viaTally && <TallyLinkCard onSetup={() => setTallyOpen(true)} onGstin={() => setGstinOpen(true)} tick={tick} onCounts={onCounts} />}
            {cfg.can_settings && !cfg.units.length && <p className="text-sm text-amber-700" data-testid="ewb-no-units">No sender units yet — add your GSTIN and address in <b>Sender units</b>.</p>}
            <div className="flex flex-wrap gap-1 border-b border-[#E5DFD0]" data-testid="ewb-tabs">
              {BILL_TABS.map((t) => (
                <TabBtn key={t.id} on={curTab === t.id} onClick={() => setTab(t.id)} testId={`ewb-tab-${t.id}`}>
                  {t.label}{rows && <span className="ml-1.5 rounded-full bg-slate-200 px-1.5 text-[11px] font-bold tabular-nums text-slate-700" data-testid={`ewb-tab-count-${t.id}`}>{rows.filter(t.has).length}</span>}
                </TabBtn>
              ))}
              <TabBtn on={curTab === 'holds'} onClick={() => setTab('holds')} testId="ewb-tab-holds">
                Needs attention{holdCount ? <span className="ml-1.5 rounded-full bg-amber-500 px-1.5 text-[11px] font-bold text-white" data-testid="ewb-holds-count">{holdCount}</span> : null}
              </TabBtn>
            </div>
            {curTab === 'holds' ? <EwbHoldsPanel onOpen={(h) => openNew({ doc_type: h.doc_type || 'customer_invoice', doc_no: h.doc_no, auto: true })} onCount={setHoldCount} onChanged={() => { load(); setTick((n) => n + 1); }} tick={holdTick} /> : (
              <>
                <div className="flex flex-wrap items-center gap-2">
                  <Input type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} className="w-[160px] bg-white" data-testid="ewb-from" />
                  <span className="text-sm text-gray-500">to</span>
                  <Input type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} className="w-[160px] bg-white" data-testid="ewb-to" />
                  <select value={f.doc_type} onChange={(e) => setF({ ...f, doc_type: e.target.value })} className={sel} data-testid="ewb-type-filter">
                    <option value="">All document types</option>{cfg.doc_types.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
                  </select>
                  <Input value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder="EWB / document / customer / vehicle" className="w-[260px] bg-white" data-testid="ewb-search" />
                </div>
                {rows && <Counts rows={rows} viaTally={viaTally} portal={portal} />}
                {curTab === 'waiting' && cfg.route !== 'gsp' && rows && <PortalJsonBar rows={rows} waiting={waiting} picked={picked} setPicked={setPicked} onDone={() => { load(); setTick((n) => n + 1); }} />}
                {curTab === 'active' && rows && <BulkPrintBar rows={tabRows} picked={picked} setPicked={setPicked} onDone={load} />}
                {curTab === 'discarded' && <p className="text-xs text-gray-500" data-testid="ewb-discarded-note">Bills discarded before they got an e-way bill number, and e-way bills cancelled after generation.</p>}
                {!rows ? <Loader2 className="w-6 h-6 mx-auto my-10 animate-spin text-amber-600" />
                  : <EwbTable rows={tabRows} onOpen={(b) => setOpenId(b.id)} picked={picked} empty={billTab.empty} onPrinted={load}
                    onPick={curTab === 'active' || (curTab === 'waiting' && cfg.route !== 'gsp') ? togglePick : null}
                    canPick={curTab === 'active' ? (b) => !!b.ewb_no : isWaiting} />}
              </>
            )}
          </>
        )}
      </main>
      <NewEwbDialog open={newOpen} onClose={closeNew} cfg={cfg} prefill={prefill} onCreated={() => { load(); setTick((n) => n + 1); }} />
      <EwbDetailDialog billId={openId} cfg={cfg} refresh={refresh} onClose={() => setOpenId(null)} onChanged={() => { load(); setTick((n) => n + 1); }} />
      {viaTally && <GstinPartiesDialog open={gstinOpen} onClose={() => setGstinOpen(false)} />}
      {cfg?.can_settings && viaTally && <TallySetupDialog open={tallyOpen} onClose={() => setTallyOpen(false)} onSaved={() => { setTallyOpen(false); setTick((n) => n + 1); }} />}
      {cfg?.can_settings && <SenderUnitsDialog open={unitsOpen} onClose={() => setUnitsOpen(false)} cfg={cfg} onSaved={(units) => { setCfg({ ...cfg, units }); setUnitsOpen(false); }} />}
    </div>
  );
}
